import React, { useState, useEffect, useRef } from 'react';
import { X as IconX, Save, Copy, Map as IconMap, Trash2, Loader, Check, Download, History, FileText, Bold, Italic, Underline, Code, Strikethrough, List, ListOrdered, Quote, Smile, Link2, AlertTriangle, Edit, Paperclip, Send } from 'lucide-react';

import ReactMarkdown from 'react-markdown';

// --- IMPORTAÇÃO DOS COMPONENTES E UTILITÁRIOS ---
import StyledFileInput from '../common/StyledFileInput';
import PDFViewerModal from './PDFViewerModal';
import UserAvatar from '../common/UserAvatar';
import { copyToClipboard } from '../../utils/clipboard';
import { generatePendencyId } from '../../utils/pendencyUtils';
import { getPendencyObservationText, syncInitialPendencyComment } from '../../utils/pendencyContent';
import ConfirmDialog from '../common/ConfirmDialog';
import { logActivity, getActivityLog } from '../../services/activityLogService';
import Timeline from '../common/Timeline';
import UserCard from '../common/UserCard';

// --- BARRA DE FERRAMENTAS DO EDITOR RICO ---
const ObservationToolbar = ({ textareaRef, textSetter }) => {
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const emojis = ['👍', '❤️', '😂', '🎉', '🚀', '👀', '💬', '✅', '⚠️', '❌'];

    const insertFormatting = (type) => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = textarea.value;
        const selectedText = text.substring(start, end);

        let replacement = '';
        let cursorOffset = 0;

        const trimSpaces = (str) => {
            const leadingMatch = str.match(/^\s*/);
            const trailingMatch = str.match(/\s*$/);
            const leading = leadingMatch ? leadingMatch[0] : '';
            const trailing = trailingMatch ? trailingMatch[0] : '';
            const trimmed = str.trim();
            return { leading, trimmed, trailing };
        };

        switch (type) {
            case 'bold': {
                const { leading, trimmed, trailing } = trimSpaces(selectedText || 'texto');
                replacement = `${leading}**${trimmed}**${trailing}`;
                cursorOffset = selectedText ? 0 : 2 + trailing.length;
                break;
            }
            case 'italic': {
                const { leading, trimmed, trailing } = trimSpaces(selectedText || 'texto');
                replacement = `${leading}*${trimmed}*${trailing}`;
                cursorOffset = selectedText ? 0 : 1 + trailing.length;
                break;
            }
            case 'underline': {
                const { leading, trimmed, trailing } = trimSpaces(selectedText || 'texto');
                replacement = `${leading}<u>${trimmed}</u>${trailing}`;
                cursorOffset = selectedText ? 0 : 4 + trailing.length;
                break;
            }
            case 'code': {
                const { leading, trimmed, trailing } = trimSpaces(selectedText || 'texto');
                replacement = `${leading}\`${trimmed}\`${trailing}`;
                cursorOffset = selectedText ? 0 : 1 + trailing.length;
                break;
            }
            case 'strikethrough': {
                const { leading, trimmed, trailing } = trimSpaces(selectedText || 'texto');
                replacement = `${leading}~~${trimmed}~~${trailing}`;
                cursorOffset = selectedText ? 0 : 2 + trailing.length;
                break;
            }
            case 'quote':
                replacement = `\n> ${selectedText || 'citação'}\n`;
                cursorOffset = selectedText ? 0 : 1;
                break;
            case 'list-bullet': {
                const lines = (selectedText || 'item').split('\n');
                const formattedLines = lines.map(line => {
                    if (/^\s*[-*]\s/.test(line)) return line;
                    return `- ${line}`;
                });
                replacement = `\n${formattedLines.join('\n')}\n`;
                cursorOffset = selectedText ? 0 : 1;
                break;
            }
            case 'list-ordered': {
                const lines = (selectedText || 'item').split('\n');
                const formattedLines = lines.map((line, idx) => {
                    if (/^\s*\d+\.\s/.test(line)) return line;
                    return `${idx + 1}. ${line}`;
                });
                replacement = `\n${formattedLines.join('\n')}\n`;
                cursorOffset = selectedText ? 0 : 1;
                break;
            }
            case 'link':
                replacement = `[${selectedText || 'Link'}](https://)`;
                cursorOffset = selectedText ? 0 : 1;
                break;
            default:
                return;
        }

        const newText = text.substring(0, start) + replacement + text.substring(end);
        textSetter(newText);

        setTimeout(() => {
            textarea.focus();
            if (selectedText) {
                textarea.setSelectionRange(start, start + replacement.length);
            } else {
                const pos = start + replacement.length - cursorOffset;
                textarea.setSelectionRange(pos, pos);
            }
        }, 0);
    };

    const insertEmoji = (emoji) => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = textarea.value;

        const newText = text.substring(0, start) + emoji + text.substring(end);
        textSetter(newText);

        setTimeout(() => {
            textarea.focus();
            const pos = start + emoji.length;
            textarea.setSelectionRange(pos, pos);
        }, 0);
    };

    return (
        <div className="flex flex-wrap items-center gap-1 p-1 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-t-lg">
            <button type="button" onClick={() => insertFormatting('bold')} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300" title="Negrito"><Bold className="w-3.5 h-3.5" /></button>
            <button type="button" onClick={() => insertFormatting('italic')} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300" title="Itálico"><Italic className="w-3.5 h-3.5" /></button>
            <button type="button" onClick={() => insertFormatting('underline')} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300" title="Sublinhado"><Underline className="w-3.5 h-3.5" /></button>
            <button type="button" onClick={() => insertFormatting('code')} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300" title="Código"><Code className="w-3.5 h-3.5" /></button>
            <button type="button" onClick={() => insertFormatting('strikethrough')} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300" title="Tachado"><Strikethrough className="w-3.5 h-3.5" /></button>
            <div className="h-4 w-[1px] bg-slate-300 dark:bg-slate-600 mx-1" />
            <button type="button" onClick={() => insertFormatting('list-bullet')} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300" title="Lista com Marcadores"><List className="w-3.5 h-3.5" /></button>
            <button type="button" onClick={() => insertFormatting('list-ordered')} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300" title="Lista Numerada"><ListOrdered className="w-3.5 h-3.5" /></button>
            <button type="button" onClick={() => insertFormatting('quote')} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300" title="Citação"><Quote className="w-3.5 h-3.5" /></button>
            <div className="h-4 w-[1px] bg-slate-300 dark:bg-slate-600 mx-1" />
            <button type="button" onClick={() => insertFormatting('link')} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300" title="Inserir Link"><Link2 className="w-3.5 h-3.5" /></button>
            <div className="relative">
                <button type="button" onClick={() => setShowEmojiPicker(!showEmojiPicker)} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300" title="Inserir Emoji"><Smile className="w-3.5 h-3.5" /></button>
                {showEmojiPicker && (
                    <div className="absolute z-[100] bottom-8 left-0 flex bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg p-1.5 gap-1">
                        {emojis.map(emoji => (
                            <button key={emoji} type="button" onClick={() => { insertEmoji(emoji); setShowEmojiPicker(false); }} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded text-sm">{emoji}</button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const CommentToolbar = ({ textareaRef, textSetter, emojis, insertFormatting, insertEmoji, setActiveAttachmentSetter, setActiveAttachmentTextareaRef, setShowAttachmentPopover }) => {
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    return (
        <div className="flex items-center flex-wrap gap-0.5 p-1.5 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 border-b-0 rounded-t-lg">
            <button type="button" onClick={() => insertFormatting(textareaRef, textSetter, 'bold')} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300" title="Negrito"><Bold className="w-3.5 h-3.5" /></button>
            <button type="button" onClick={() => insertFormatting(textareaRef, textSetter, 'italic')} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300" title="Itálico"><Italic className="w-3.5 h-3.5" /></button>
            <button type="button" onClick={() => insertFormatting(textareaRef, textSetter, 'underline')} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300" title="Sublinhado"><Underline className="w-3.5 h-3.5" /></button>
            <button type="button" onClick={() => insertFormatting(textareaRef, textSetter, 'code')} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300" title="Código"><Code className="w-3.5 h-3.5" /></button>
            <button type="button" onClick={() => insertFormatting(textareaRef, textSetter, 'strikethrough')} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300" title="Tachado"><Strikethrough className="w-3.5 h-3.5" /></button>
            <div className="h-4 w-[1px] bg-slate-300 dark:bg-slate-600 mx-1" />
            <button type="button" onClick={() => insertFormatting(textareaRef, textSetter, 'list-bullet')} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300" title="Lista com Marcadores"><List className="w-3.5 h-3.5" /></button>
            <button type="button" onClick={() => insertFormatting(textareaRef, textSetter, 'list-ordered')} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300" title="Lista Numerada"><ListOrdered className="w-3.5 h-3.5" /></button>
            <button type="button" onClick={() => insertFormatting(textareaRef, textSetter, 'quote')} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300" title="Citação"><Quote className="w-3.5 h-3.5" /></button>
            <div className="h-4 w-[1px] bg-slate-300 dark:bg-slate-600 mx-1" />
            <button type="button" onClick={() => insertFormatting(textareaRef, textSetter, 'link')} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300" title="Inserir Link"><Link2 className="w-3.5 h-3.5" /></button>
            <button
                type="button"
                onClick={() => { setActiveAttachmentSetter(() => textSetter); setActiveAttachmentTextareaRef(textareaRef); setShowAttachmentPopover(true); }}
                className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300"
                title="Anexar item do sistema"
            >
                <Paperclip className="w-3.5 h-3.5" />
            </button>
            <div className="relative">
                <button type="button" onClick={() => setShowEmojiPicker(!showEmojiPicker)} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300" title="Inserir Emoji"><Smile className="w-3.5 h-3.5" /></button>
                {showEmojiPicker && (
                    <div className="absolute z-[100] bottom-8 left-0 flex bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg p-1.5 gap-1">
                        {emojis.map(emoji => (
                            <button key={emoji} type="button" onClick={() => { insertEmoji(textareaRef, textSetter, emoji); setShowEmojiPicker(false); }} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded text-sm">{emoji}</button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const PendencyModal = ({ pendency, onClose, onSave, onUpdate, onDelete, cities, users, currentUser, isProcessing, onFileDownload, onDeleteFile, setModalMessage, theme, onUpdatePendencyField, usersMap, allProjects, onOpenProject, allPendencies, onOpenPendency, onAddComment, onUpdateComment, onDeleteComment }) => {
    
    const isViewMode = !!pendency;
    const userRole = currentUser.role;
    const canEdit = userRole === 'administrador' || userRole === 'tecnico' || userRole === 'tecnico_campo';
    const isFieldTech = userRole === 'tecnico_campo';
    const canSetPendencyType = userRole === 'administrador' || userRole === 'tecnico';

    const pendencyIdRef = useRef(null);
    const observacoesTextareaRef = useRef(null);

    const [formData, setFormData] = useState({});
    const [files, setFiles] = useState([]);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [downloading, setDownloading] = useState(null);
    const [copySuccess, setCopySuccess] = useState(false);
    const [copyIdSuccess, setCopyIdSuccess] = useState(false);
    const [isPdfViewerOpen, setIsPdfViewerOpen] = useState(false);
    const [pdfFile, setPdfFile] = useState(null);
    const [activeTab, setActiveTab] = useState('details');
    const [activities, setActivities] = useState([]);
    const [isHistoryLoading, setIsHistoryLoading] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    // Referências para os textareas
    const newCommentTextareaRef = useRef(null);
    const replyTextareaRef = useRef(null);
    const editCommentTextareaRef = useRef(null);

    // Estados de menções e comentários aninhados
    const [mentionSearch, setMentionSearch] = useState('');
    const [mentionStartIndex, setMentionStartIndex] = useState(-1);
    const [showMentionDropdown, setShowMentionDropdown] = useState(false);
    const [selectedUserIndex, setSelectedUserIndex] = useState(0);
    const [activeTextareaRef, setActiveTextareaRef] = useState(null);
    const [activeTextSetter, setActiveTextSetter] = useState(null);
    const [replyingToCommentId, setReplyingToCommentId] = useState(null);
    const [replyCommentText, setReplyCommentText] = useState('');

    // Estados para anexar itens do sistema
    const [showAttachmentPopover, setShowAttachmentPopover] = useState(false);
    const [attachmentType, setAttachmentType] = useState('project'); // 'project' ou 'pendency'
    const [attachmentSearch, setAttachmentSearch] = useState('');
    const [activeAttachmentSetter, setActiveAttachmentSetter] = useState(null);
    const [activeAttachmentTextareaRef, setActiveAttachmentTextareaRef] = useState(null);

    // Emojis e lista de usuários
    const emojis = ['👍', '❤️', '😂', '🎉', '🚀', '👀', '💬', '✅', '⚠️', '❌'];
    const [newComment, setNewComment] = useState('');
    const [editingComment, setEditingComment] = useState(null);

    useEffect(() => {
        if (isViewMode && pendency) {
            if (currentUser.role === 'tecnico_campo' && pendency.tipo === 'Administrativa') {
                onClose();
                setModalMessage('Você não tem permissão para visualizar esta pendência.');
                return;
            }

            logActivity('view', 'pendency', pendency.id, null, generatePendencyId(pendency.id));
            pendencyIdRef.current = pendency.id;

            let executadoPorValue = '';
            if (pendency.executadoPor) {
                const assignedUserId = typeof pendency.executadoPor === 'object' 
                    ? pendency.executadoPor.uid 
                    : pendency.executadoPor;

                const assignedUser = users.find(u => u.id === assignedUserId);

                if (assignedUser) {
                    executadoPorValue = JSON.stringify({ uid: assignedUser.id, displayName: assignedUser.displayName });
                }
            }

            setFormData({
                cidade: pendency.cidade || '',
                descricao: pendency.descricao || '',
                prioridade: pendency.prioridade || 'Média',
                status: pendency.status || 'Pendente',
                dataCadastro: pendency.dataCadastro || new Date().toISOString(),
                cadastradoPor: pendency.cadastradoPor || { uid: currentUser.uid, displayName: currentUser.displayName },
                dataExecucao: pendency.dataExecucao ? new Date(pendency.dataExecucao).toISOString().split('T')[0] : '',
                executadoPor: executadoPorValue,
                observacoes: getPendencyObservationText(pendency),
                coordenadas: pendency.coordenadas || '',
                tipo: pendency.tipo || 'Externa'
            });
        } else {
            pendencyIdRef.current = null;
            setFormData({
                cidade: cities[0]?.nome || '',
                descricao: '',
                prioridade: 'Média',
                status: 'Pendente',
                dataCadastro: new Date().toISOString(),
                cadastradoPor: { uid: currentUser.uid, displayName: currentUser.displayName },
                dataExecucao: '',
                executadoPor: '',
                observacoes: '',
                coordenadas: '',
                tipo: 'Externa'
            });
        }
    }, [pendency, isViewMode, currentUser, cities, users, onClose, setModalMessage]);

    useEffect(() => {
        if (isViewMode && activeTab === 'history') {
            const fetchActivities = async () => {
                setIsHistoryLoading(true);
                const log = await getActivityLog('pendency', pendency.id);

                const viewedUsers = new Set();
                const filteredLog = log
                    .slice()
                    .reverse()
                    .filter(activity => {
                        if (activity.activityType !== 'view') {
                            return true;
                        }
                        if (viewedUsers.has(activity.userId)) {
                            return false;
                        }
                        viewedUsers.add(activity.userId);
                        return true;
                    })
                    .reverse();

                setActivities(filteredLog);
                setIsHistoryLoading(false);
            };
            fetchActivities();
        }
    }, [isViewMode, activeTab, pendency]);


    useEffect(() => {
        if (formData.status !== 'Executado') {
            setFormData(prev => ({ ...prev, dataExecucao: '' }));
        }
    }, [formData.status]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleTypeChange = (isChecked) => {
        const newType = isChecked ? 'Administrativa' : 'Externa';
        setFormData(prev => ({ ...prev, tipo: newType }));

        if (isViewMode && pendencyIdRef.current) {
            onUpdatePendencyField(pendencyIdRef.current, 'tipo', newType);
        }
    };

    const handleFileChange = (e) => {
        setFiles(Array.from(e.target.files));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (formData.status === 'Executado' && (!formData.executadoPor || !formData.dataExecucao)) {
            setModalMessage("Para marcar como 'Executado', é obrigatório atribuir um técnico e selecionar a data de execução.");
            return;
        }

        if (isFieldTech && formData.status === 'Executado') {
            const assignedUser = formData.executadoPor ? JSON.parse(formData.executadoPor) : null;
            if (!assignedUser || assignedUser.uid !== currentUser.uid) {
                setModalMessage("Você só pode marcar como 'Executado' uma pendência que está atribuída a você.");
                return;
            }
        }

        const dataToSave = {
            ...formData,
            executadoPor: formData.executadoPor ? JSON.parse(formData.executadoPor) : null,
        };

        if (isViewMode) {
            const id = pendencyIdRef.current;
            if (!id) {
                console.error("Erro Crítico: Tentativa de atualizar sem um ID de pendência. A operação foi cancelada.");
                return;
            }

            const oldObservations = getPendencyObservationText(pendency);
            const newObservations = formData.observacoes || '';

            if (oldObservations !== newObservations) {
                let activityType = 'update_observation';
                if (!oldObservations && newObservations) {
                    activityType = 'create_observation';
                } else if (oldObservations && !newObservations) {
                    activityType = 'delete_observation';
                }

                logActivity(activityType, 'pendency', id, {
                    old: oldObservations,
                    new: newObservations
                }, pendencyId);

                dataToSave.comments = syncInitialPendencyComment(
                    pendency,
                    newObservations,
                    { uid: currentUser.uid, displayName: currentUser.displayName }
                );
            }

            onUpdate(id, dataToSave, files, pendency.status);
        } else {
            // Quando uma nova pendência é salva, o log da observação inicial
            // deve ser tratado na função `handleSave` da página pai,
            // após o ID da nova pendência ser gerado.
            onSave(dataToSave, files);
        }
    };

    const handleDelete = () => {
        const id = pendencyIdRef.current;
        if (id) {
            onDelete(id);
        }
    };
    
    const handleDownloadClick = async (file, downloadKey) => {
        if (!file.path || downloading) return;

        const isPdf = file.name && file.name.toLowerCase().endsWith('.pdf');

        if (isPdf) {
            setPdfFile(file);
            setIsPdfViewerOpen(true);
        } else {
            setDownloading(downloadKey);
            await onFileDownload(file.path, file.name);
            setDownloading(null);
        }
    };

    const handleDeleteFile = (file) => {
        const id = pendencyIdRef.current;
        if (id) {
            onDeleteFile(id, file);
        }
    };

    const handleShare = async () => {
        if (!formData.coordenadas) return;
        const mapsLink = `https://www.google.com/maps?q=${formData.coordenadas.trim()}`;
        const textToCopy = `*Detalhes da Pendência*\n- *Descrição:* ${formData.descricao}\n- *Cidade:* ${formData.cidade}\n- *Prioridade:* ${formData.prioridade}\n- *Link do Mapa:* ${mapsLink}`;

        const success = await copyToClipboard(textToCopy);
        if (success) {
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000); 
        }
    };

    const pendencyId = isViewMode ? generatePendencyId(pendency.id) : '';

    const handleCopyId = async (e) => {
        e.stopPropagation();
        if (!pendencyId) return;
        const success = await copyToClipboard(pendencyId);
        if (success) {
            setCopyIdSuccess(true);
            setTimeout(() => setCopyIdSuccess(false), 2000);
        }
    };

    const handleOpenMap = () => {
        if (!formData.coordenadas) return;
        const mapsLink = `https://www.google.com/maps?q=${formData.coordenadas.trim()}`;
        window.open(mapsLink, '_blank', 'noopener,noreferrer');
    };

    const usersList = React.useMemo(() => {
        if (usersMap) return Array.from(usersMap.values());
        return users || [];
    }, [usersMap, users]);

    const filteredUsers = React.useMemo(() => {
        if (!mentionSearch) return usersList;
        const searchLower = mentionSearch.toLowerCase();
        return usersList.filter(user => 
            (user.displayName && user.displayName.toLowerCase().includes(searchLower)) ||
            (user.email && user.email.toLowerCase().includes(searchLower))
        );
    }, [usersList, mentionSearch]);

    const filteredAttachments = React.useMemo(() => {
        const search = attachmentSearch.toLowerCase();
        if (attachmentType === 'project') {
            return (allProjects || []).filter(p =>
                ((p.protocolo && p.protocolo.toLowerCase().includes(search)) ||
                 (p.cidade && p.cidade.toLowerCase().includes(search)) ||
                 (p.local && p.local.toLowerCase().includes(search)))
            );
        } else {
            return (allPendencies || []).filter(p =>
                p.id !== pendency?.id &&
                ((p.descricao && p.descricao.toLowerCase().includes(search)) ||
                 (p.cidade && p.cidade.toLowerCase().includes(search)))
            );
        }
    }, [allProjects, allPendencies, attachmentSearch, attachmentType, pendency?.id]);

    const handleOpenProjectFromLink = React.useCallback((projId) => {
        if (!allProjects || !onOpenProject) return;
        const targetProject = allProjects.find(p => p.id === projId);
        if (targetProject) {
            onClose(); // fecha o modal de pendências
            onOpenProject(targetProject);
        } else {
            alert('Projeto associado ao anexo não foi encontrado ou foi deletado.');
        }
    }, [allProjects, onOpenProject, onClose]);

    const handleOpenPendencyFromLink = React.useCallback((pendId) => {
        if (!allPendencies || !onOpenPendency) return;
        const targetPendency = allPendencies.find(p => p.id === pendId);
        if (targetPendency) {
            onOpenPendency(targetPendency);
        } else {
            alert('Pendência associada ao anexo não foi encontrada ou foi deletada.');
        }
    }, [allPendencies, onOpenPendency]);

    // Componentes personalizados para o ReactMarkdown
    const markdownComponents = React.useMemo(() => ({
        a: ({ href, children, ...props }) => {
            if (href === 'u-link') {
                return <u>{children}</u>;
            }
            if (href === 'del-link') {
                return <del>{children}</del>;
            }
            if (href && href.startsWith('https://mention.user/')) {
                const name = decodeURIComponent(href.substring(21));
                return (
                    <span className="bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 font-bold px-1.5 py-0.5 rounded text-xs inline-block mx-0.5 select-none">
                        @{name}
                    </span>
                );
            }
            if (href && (href.startsWith('https://project.link/') || href.startsWith('project:'))) {
                const projId = href.startsWith('project:') ? href.substring(8) : href.substring(21);
                return (
                    <button
                        type="button"
                        onClick={() => handleOpenProjectFromLink(projId)}
                        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors font-semibold text-[11px] my-0.5 cursor-pointer shadow-sm select-none"
                    >
                        <FileText className="w-3 h-3" />
                        {children}
                    </button>
                );
            }
            if (href && (href.startsWith('https://pendency.link/') || href.startsWith('pendency:'))) {
                const pendId = href.startsWith('pendency:') ? href.substring(9) : href.substring(22);
                return (
                    <button
                        type="button"
                        onClick={() => handleOpenPendencyFromLink(pendId)}
                        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-purple-50 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-800 hover:bg-purple-100 dark:hover:bg-purple-900/50 transition-colors font-semibold text-[11px] my-0.5 cursor-pointer shadow-sm select-none"
                    >
                        <AlertTriangle className="w-3 h-3" />
                        {children}
                    </button>
                );
            }
            return <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline font-semibold" {...props}>{children}</a>;
        },
        p: ({ children }) => <p className="my-1 leading-relaxed">{children}</p>,
        li: ({ children }) => <li className="my-0.5 leading-relaxed">{children}</li>,
        blockquote: ({ children }) => <blockquote className="border-l-4 border-slate-300 dark:border-slate-600 pl-3 italic my-2 text-slate-500 dark:text-slate-400">{children}</blockquote>,
        h1: ({ children }) => <h1 className="text-base font-bold my-1 text-slate-800 dark:text-slate-200">{children}</h1>,
        h2: ({ children }) => <h2 className="text-sm font-bold my-1 text-slate-800 dark:text-slate-200">{children}</h2>,
        h3: ({ children }) => <h3 className="text-xs font-bold my-1 text-slate-800 dark:text-slate-200">{children}</h3>,
    }), [handleOpenProjectFromLink, handleOpenPendencyFromLink]);

    const insertFormatting = (textareaRef, textSetter, type) => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = textarea.value;
        const selectedText = text.substring(start, end);

        let replacement = '';
        let cursorOffset = 0;

        const trimSpaces = (str) => {
            const leadingMatch = str.match(/^\s*/);
            const trailingMatch = str.match(/\s*$/);
            const leading = leadingMatch ? leadingMatch[0] : '';
            const trailing = trailingMatch ? trailingMatch[0] : '';
            const trimmed = str.trim();
            return { leading, trimmed, trailing };
        };

        switch (type) {
            case 'bold': {
                const { leading, trimmed, trailing } = trimSpaces(selectedText || 'texto');
                replacement = `${leading}**${trimmed}**${trailing}`;
                cursorOffset = selectedText ? 0 : 2 + trailing.length;
                break;
            }
            case 'italic': {
                const { leading, trimmed, trailing } = trimSpaces(selectedText || 'texto');
                replacement = `${leading}*${trimmed}*${trailing}`;
                cursorOffset = selectedText ? 0 : 1 + trailing.length;
                break;
            }
            case 'underline': {
                const { leading, trimmed, trailing } = trimSpaces(selectedText || 'texto');
                replacement = `${leading}<u>${trimmed}</u>${trailing}`;
                cursorOffset = selectedText ? 0 : 4 + trailing.length;
                break;
            }
            case 'code': {
                const { leading, trimmed, trailing } = trimSpaces(selectedText || 'texto');
                replacement = `${leading}\`${trimmed}\`${trailing}`;
                cursorOffset = selectedText ? 0 : 1 + trailing.length;
                break;
            }
            case 'strikethrough': {
                const { leading, trimmed, trailing } = trimSpaces(selectedText || 'texto');
                replacement = `${leading}~~${trimmed}~~${trailing}`;
                cursorOffset = selectedText ? 0 : 2 + trailing.length;
                break;
            }
            case 'quote':
                replacement = `\n> ${selectedText || 'citação'}\n`;
                cursorOffset = selectedText ? 0 : 1;
                break;
            case 'list-bullet': {
                const lines = (selectedText || 'item').split('\n');
                const formattedLines = lines.map(line => {
                    if (/^\s*[-*]\s/.test(line)) return line;
                    return `- ${line}`;
                });
                replacement = `\n${formattedLines.join('\n')}\n`;
                cursorOffset = selectedText ? 0 : 1;
                break;
            }
            case 'list-ordered': {
                const lines = (selectedText || 'item').split('\n');
                const formattedLines = lines.map((line, idx) => {
                    if (/^\s*\d+\.\s/.test(line)) return line;
                    return `${idx + 1}. ${line}`;
                });
                replacement = `\n${formattedLines.join('\n')}\n`;
                cursorOffset = selectedText ? 0 : 1;
                break;
            }
            case 'link':
                replacement = `[${selectedText || 'Link'}](https://)`;
                cursorOffset = selectedText ? 0 : 1;
                break;
            default:
                return;
        }

        const newText = text.substring(0, start) + replacement + text.substring(end);
        textSetter(newText);

        setTimeout(() => {
            textarea.focus();
            if (selectedText) {
                textarea.setSelectionRange(start, start + replacement.length);
            } else {
                const pos = start + replacement.length - cursorOffset;
                textarea.setSelectionRange(pos, pos);
            }
        }, 0);
    };

    const insertEmoji = (textareaRef, textSetter, emoji) => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = textarea.value;

        const newText = text.substring(0, start) + emoji + text.substring(end);
        textSetter(newText);

        setTimeout(() => {
            textarea.focus();
            const pos = start + emoji.length;
            textarea.setSelectionRange(pos, pos);
        }, 0);
    };

    const handleInsertAttachment = (item) => {
        const textarea = activeAttachmentTextareaRef?.current;
        if (!textarea || !activeAttachmentSetter) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = textarea.value;

        let markdown = '';
        if (attachmentType === 'project') {
            markdown = `[📎 Projeto: ${item.protocolo} - ${item.cidade || ''}](https://project.link/${item.id})`;
        } else {
            const pendencyIdStr = item.id.substring(0, 5).toUpperCase();
            markdown = `[📎 Pendência: #${pendencyIdStr} - ${item.descricao.substring(0, 20)}...](https://pendency.link/${item.id})`;
        }

        const newText = text.substring(0, start) + markdown + text.substring(end);
        activeAttachmentSetter(newText);
        setShowAttachmentPopover(false);
        setAttachmentSearch('');

        setTimeout(() => {
            textarea.focus();
            const pos = start + markdown.length;
            textarea.setSelectionRange(pos, pos);
        }, 0);
    };

    const handleTextareaChange = (e, textSetter, textareaRef) => {
        const text = e.target.value;
        textSetter(text);
        setActiveTextareaRef(textareaRef);
        setActiveTextSetter(() => textSetter);
        
        const selectionStart = e.target.selectionStart;
        const textBeforeCursor = text.substring(0, selectionStart);
        const lastAtIdx = textBeforeCursor.lastIndexOf('@');
        
        if (lastAtIdx !== -1) {
            const textAfterAt = textBeforeCursor.substring(lastAtIdx + 1);
            const charBeforeAt = lastAtIdx > 0 ? textBeforeCursor[lastAtIdx - 1] : '';
            const isValidStart = lastAtIdx === 0 || /\s/.test(charBeforeAt);
            
            if (isValidStart && !/\s/.test(textAfterAt)) {
                setShowMentionDropdown(true);
                setMentionSearch(textAfterAt);
                setMentionStartIndex(lastAtIdx);
                setSelectedUserIndex(0);
                return;
            }
        }
        setShowMentionDropdown(false);
    };

    const handleTextareaKeyDown = (e, filteredUsers, textSetter) => {
        if (showMentionDropdown && filteredUsers.length > 0) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedUserIndex(prev => (prev + 1) % filteredUsers.length);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedUserIndex(prev => (prev - 1 + filteredUsers.length) % filteredUsers.length);
            } else if (e.key === 'Enter') {
                e.preventDefault();
                handleSelectMention(filteredUsers[selectedUserIndex]);
            } else if (e.key === 'Escape') {
                e.preventDefault();
                setShowMentionDropdown(false);
            }
            return;
        }

        if (e.key === 'Enter' && textSetter) {
            const textarea = e.target;
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const text = textarea.value;

            const beforeCursor = text.substring(0, start);
            const lastNewLine = beforeCursor.lastIndexOf('\n');
            const currentLine = beforeCursor.substring(lastNewLine + 1);

            const bulletRegex = /^(\s*[-*]\s)(.*)/;
            const orderedRegex = /^(\s*(\d+)\.\s)(.*)/;

            const bulletMatch = currentLine.match(bulletRegex);
            const orderedMatch = currentLine.match(orderedRegex);

            if (bulletMatch || orderedMatch) {
                if (e.shiftKey) {
                    e.preventDefault();
                    const indentSize = bulletMatch ? bulletMatch[1].length : orderedMatch[1].length;
                    const indent = ' '.repeat(indentSize);
                    const replacement = '\n' + indent;

                    const newText = text.substring(0, start) + replacement + text.substring(end);
                    textSetter(newText);

                    setTimeout(() => {
                        textarea.focus();
                        const newPos = start + replacement.length;
                        textarea.setSelectionRange(newPos, newPos);
                    }, 0);
                } else {
                    e.preventDefault();
                    const content = bulletMatch ? bulletMatch[2] : orderedMatch[3];
                    
                    if (content.trim() === '') {
                        const beforeLine = text.substring(0, lastNewLine + 1);
                        const afterCursor = text.substring(start);
                        const newText = beforeLine + afterCursor;
                        textSetter(newText);

                        setTimeout(() => {
                            textarea.focus();
                            const newPos = lastNewLine + 1;
                            textarea.setSelectionRange(newPos, newPos);
                        }, 0);
                    } else {
                        let nextMarker = '';
                        if (bulletMatch) {
                            nextMarker = '\n' + bulletMatch[1];
                        } else {
                            const currentNum = parseInt(orderedMatch[2], 10);
                            const nextNum = currentNum + 1;
                            const spacesMatch = orderedMatch[1].match(/^\s*/);
                            const initialSpaces = spacesMatch ? spacesMatch[0] : '';
                            nextMarker = `\n${initialSpaces}${nextNum}. `;
                        }

                        const newText = text.substring(0, start) + nextMarker + text.substring(end);
                        textSetter(newText);

                        setTimeout(() => {
                            textarea.focus();
                            const newPos = start + nextMarker.length;
                            textarea.setSelectionRange(newPos, newPos);
                        }, 0);
                    }
                }
            }
        }
    };

    const handleSelectMention = (user) => {
        const textarea = activeTextareaRef?.current;
        if (!textarea || !activeTextSetter) return;
        
        const text = textarea.value;
        const selectionStart = textarea.selectionStart;
        const beforeMention = text.substring(0, mentionStartIndex);
        const afterMention = text.substring(selectionStart);
        const mentionText = `@${user.displayName} `;
        const newText = beforeMention + mentionText + afterMention;
        
        activeTextSetter(newText);
        setShowMentionDropdown(false);
        
        setTimeout(() => {
            textarea.focus();
            const cursorPosition = mentionStartIndex + mentionText.length;
            textarea.setSelectionRange(cursorPosition, cursorPosition);
        }, 0);
    };

    const extractMentionedUids = React.useCallback((text) => {
        if (!text) return [];
        return usersList
            .filter(user => user.displayName && text.includes(`@${user.displayName}`))
            .map(user => user.id || user.uid)
            .filter(Boolean);
    }, [usersList]);

    const preProcessText = React.useCallback((text) => {
        if (!text) return '';
        let processed = text;
        
        processed = processed.replace(/<u>(.*?)<\/u>/g, '[$1](u-link)');
        processed = processed.replace(/<del>(.*?)<\/del>/g, '[$1](del-link)');
        processed = processed.replace(/~~(.*?)~~/g, '[$1](del-link)');
        
        usersList.forEach(user => {
            if (user.displayName) {
                const name = user.displayName;
                const escapedName = name.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
                const regex = new RegExp(`@${escapedName}\\b`, 'g');
                processed = processed.replace(regex, `[@${name}](https://mention.user/${encodeURIComponent(name)})`);
            }
        });
        
        return processed;
    }, [usersList]);

    const handleCommentSubmit = (parentId = null, text = newComment, textSetter = setNewComment) => {
        const id = pendencyIdRef.current;
        if (text.trim() === '' || !id) return;
        
        const mentionedUids = extractMentionedUids(text);
        onAddComment(id, text, parentId, mentionedUids);
        textSetter('');
        setReplyingToCommentId(null);
    };

    const handleEditComment = (comment) => {
        setEditingComment({ ...comment });
    };

    const handleSaveEditedComment = () => {
        const id = pendencyIdRef.current;
        if (!editingComment || editingComment.text.trim() === '' || !id) return;
        
        const mentionedUids = extractMentionedUids(editingComment.text);
        onUpdateComment(id, editingComment, mentionedUids);
        setEditingComment(null);
    };

    const organizedComments = React.useMemo(() => {
        if (!pendency?.comments) return [];
        
        const commentsList = [...pendency.comments];
        const commentsMap = {};
        const rootComments = [];
        
        commentsList.forEach(c => {
            const id = c.id || c.createdAt;
            commentsMap[id] = { ...c, replies: [] };
        });
        
        commentsList.forEach(c => {
            const id = c.id || c.createdAt;
            const current = commentsMap[id];
            
            if (c.parentId && commentsMap[c.parentId]) {
                commentsMap[c.parentId].replies.push(current);
            } else {
                rootComments.push(current);
            }
        });
        
        const sortByDate = (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        
        rootComments.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        
        rootComments.forEach(root => {
            root.replies.sort(sortByDate);
            root.replies.forEach(reply => {
                reply.replies.sort(sortByDate);
            });
        });
        
        return rootComments;
    }, [pendency?.comments]);

    const renderMentionDropdown = (textareaRef) => {
        if (showMentionDropdown && activeTextareaRef === textareaRef && filteredUsers.length > 0) {
            return (
                <div className="absolute z-[200] max-h-40 w-64 overflow-y-auto bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl mt-1 scrollbar-thin">
                    {filteredUsers.map((user, idx) => (
                        <button
                            key={user.uid || user.id}
                            type="button"
                            onClick={() => handleSelectMention(user)}
                            className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 transition-colors ${idx === selectedUserIndex ? 'bg-blue-500 text-white font-semibold' : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200'}`}
                        >
                            <UserAvatar user={user} size="h-5 w-5" />
                            <span className="truncate">{user.displayName}</span>
                        </button>
                    ))}
                </div>
            );
        }
        return null;
    };

    const getUserFromMapOrList = (uid) => {
        if (usersMap && usersMap.has(uid)) return usersMap.get(uid);
        return usersList.find(u => u.uid === uid || u.id === uid);
    };

    const onDeleteCommentHandler = (comment) => {
        const id = pendencyIdRef.current;
        if (!id) return;
        onDeleteComment(id, comment);
    };

    const renderCommentNode = (comment, level = 0) => {
        const isAuthor = currentUser.uid === comment.author?.uid;
        const canDelete = isAuthor || currentUser.role === 'administrador';
        const commentId = comment.id || comment.createdAt;
        
        const isEditing = !!editingComment && (
            (editingComment.id && comment.id) ? editingComment.id === comment.id : editingComment.createdAt === comment.createdAt
        );
        const isReplying = !!replyingToCommentId && replyingToCommentId === commentId;
        
        const indentClass = level === 1 ? 'ml-6 sm:ml-10 border-l border-slate-200 dark:border-slate-800 pl-4 sm:pl-6' : level === 2 ? 'ml-12 sm:ml-20 border-l border-slate-200 dark:border-slate-800 pl-4 sm:pl-6' : '';

        return (
            <div key={commentId} className={`flex flex-col gap-2 ${indentClass} mt-3`}>
                <div className="flex gap-3">
                    <UserAvatar user={getUserFromMapOrList(comment.author?.uid)} size="h-7 w-7" />
                    <div className="flex-1 bg-slate-50 dark:bg-slate-800/40 rounded-xl p-3 border border-slate-200/50 dark:border-slate-700/30">
                        <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                                <p className="font-bold text-xs text-slate-800 dark:text-slate-200">{comment.author?.displayName}</p>
                                {level > 0 && (
                                    <span className="text-[9px] bg-slate-200/60 dark:bg-slate-700 text-slate-500 dark:text-slate-400 px-1.5 py-0.2 rounded-full font-semibold">
                                        Resposta
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                <p className="text-[10px] text-slate-400 dark:text-slate-500">{new Date(comment.createdAt).toLocaleString('pt-BR')}</p>
                                {!isEditing && (
                                    <div className="flex items-center gap-1">
                                        {level < 2 && (
                                            <button 
                                                type="button" 
                                                onClick={() => { setReplyingToCommentId(commentId); setReplyCommentText(''); }}
                                                className="text-[10px] text-blue-500 hover:text-blue-600 font-bold px-1.5 py-0.5 rounded hover:bg-blue-50 dark:hover:bg-blue-900/10"
                                            >
                                                Responder
                                            </button>
                                        )}
                                        {isAuthor && (
                                            <button type="button" aria-label="Editar comentário" title="Editar comentário" onClick={() => handleEditComment(comment)} className="text-slate-400 hover:text-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded p-1"><Edit className="h-3 w-3"/></button>
                                        )}
                                        {canDelete && (
                                            <button type="button" aria-label="Excluir comentário" title="Excluir comentário" onClick={() => onDeleteCommentHandler(comment)} className="text-slate-400 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-red-500 rounded p-1"><Trash2 className="h-3 w-3"/></button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        {isEditing ? (
                            <div className="relative mt-1">
                                <CommentToolbar 
                                    textareaRef={editCommentTextareaRef} 
                                    textSetter={(text) => setEditingComment(prev => ({ ...prev, text }))}
                                    emojis={emojis}
                                    insertFormatting={insertFormatting}
                                    insertEmoji={insertEmoji}
                                    setActiveAttachmentSetter={setActiveAttachmentSetter}
                                    setActiveAttachmentTextareaRef={setActiveAttachmentTextareaRef}
                                    setShowAttachmentPopover={setShowAttachmentPopover}
                                />
                                <textarea 
                                    ref={editCommentTextareaRef}
                                    value={editingComment.text} 
                                    onChange={(e) => handleTextareaChange(e, (text) => setEditingComment(prev => ({ ...prev, text })), editCommentTextareaRef)}
                                    onKeyDown={(e) => handleTextareaKeyDown(e, filteredUsers, (text) => setEditingComment(prev => ({ ...prev, text })))}
                                    className="input-style px-4 w-full rounded-t-none border-t-0 focus:ring-0 text-xs" 
                                    rows="3"
                                />
                                {renderMentionDropdown(editCommentTextareaRef)}
                                <div className="flex gap-2 mt-2">
                                    <button type="button" onClick={handleSaveEditedComment} className="btn-primary text-[10px] py-1 px-2.5">Salvar</button>
                                    <button type="button" onClick={() => setEditingComment(null)} className="btn-secondary text-[10px] py-1 px-2.5">Cancelar</button>
                                </div>
                            </div>
                        ) : (
                            <div className="prose prose-sm dark:prose-invert max-w-none text-slate-700 dark:text-slate-300 text-xs mt-1">
                                <ReactMarkdown components={markdownComponents}>{preProcessText(comment.text)}</ReactMarkdown>
                            </div>
                        )}
                    </div>
                </div>

                {isReplying && (
                    <div className="ml-8 flex gap-3 mt-1">
                        <UserAvatar user={currentUser} size="h-6 w-6" />
                        <div className="flex-1 relative">
                            <CommentToolbar 
                                textareaRef={replyTextareaRef} 
                                textSetter={setReplyCommentText}
                                emojis={emojis}
                                insertFormatting={insertFormatting}
                                insertEmoji={insertEmoji}
                                setActiveAttachmentSetter={setActiveAttachmentSetter}
                                setActiveAttachmentTextareaRef={setActiveAttachmentTextareaRef}
                                setShowAttachmentPopover={setShowAttachmentPopover}
                            />
                            <textarea
                                ref={replyTextareaRef}
                                value={replyCommentText}
                                onChange={(e) => handleTextareaChange(e, setReplyCommentText, replyTextareaRef)}
                                onKeyDown={(e) => handleTextareaKeyDown(e, filteredUsers, setReplyCommentText)}
                                placeholder={`Responder para ${comment.author?.displayName}...`}
                                rows="2"
                                className="input-style px-4 w-full rounded-t-none border-t-0 focus:ring-0 text-xs"
                                autoFocus
                            />
                            {renderMentionDropdown(replyTextareaRef)}
                            <div className="flex gap-2 mt-2">
                                <button
                                    type="button"
                                    disabled={!replyCommentText.trim() || isProcessing}
                                    onClick={() => handleCommentSubmit(commentId, replyCommentText, setReplyCommentText)}
                                    className="btn-primary text-[10px] py-1 px-2.5 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isProcessing && <Loader className="animate-spin h-3 w-3 mr-1" />}
                                    Responder
                                </button>
                                <button type="button" onClick={() => { setReplyingToCommentId(null); setReplyCommentText(''); }} className="btn-secondary text-[10px] py-1 px-2.5">
                                    Cancelar
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {comment.replies && comment.replies.length > 0 && (
                    <div className="flex flex-col gap-1">
                        {comment.replies.map(reply => renderCommentNode(reply, level + 1))}
                    </div>
                )}
            </div>
        );
    };

    const handleUserAvatarClick = (user) => {
        setSelectedUser(user);
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-[1050] flex justify-center items-center p-4">
            <UserCard user={selectedUser} onClose={() => setSelectedUser(null)} />
            <ConfirmDialog
                isOpen={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                onConfirm={handleDelete}
                title="Confirmar Exclusão"
                message="Você tem certeza que deseja excluir esta pendência? Esta ação não pode ser desfeita."
                isProcessing={isProcessing}
            />
            <PDFViewerModal
                isOpen={isPdfViewerOpen}
                onClose={() => setIsPdfViewerOpen(false)}
                onDownload={() => onFileDownload(pdfFile.path, pdfFile.name)}
                fileUrl={pdfFile?.url}
                fileName={pdfFile?.name}
            />
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
                <header className="p-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
                    <div className="flex items-center gap-2">
                        <h2 className="text-xl font-bold">
                            {isViewMode ? `Detalhes da Pendência #${pendencyId}` : 'Cadastrar Nova Pendência'}
                        </h2>
                        {isViewMode && (
                            <button
                                onClick={handleCopyId}
                                title={copyIdSuccess ? "ID copiado!" : "Copiar ID da Pendência"}
                                aria-label={copyIdSuccess ? "ID copiado!" : "Copiar ID da Pendência"}
                                className="p-1 rounded-full text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-slate-400"
                            >
                                {copyIdSuccess ? (
                                    <Check className="h-4 w-4 text-green-500" />
                                ) : (
                                    <Copy className="h-4 w-4" />
                                )}
                            </button>
                        )}
                    </div>
                    <button onClick={onClose} aria-label="Fechar" title="Fechar" className="p-2 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-400"><IconX /></button>
                </header>

                {isViewMode && (
                    <div className="border-b border-slate-200 dark:border-slate-700">
                        <nav className="flex" role="tablist" aria-label="Abas da pendência">
                            <button
                                role="tab"
                                aria-selected={activeTab === 'details'}
                                aria-controls="tabpanel-details"
                                onClick={() => setActiveTab('details')}
                                className={`flex-1 py-3 px-1 text-sm font-medium flex items-center justify-center border-b-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:bg-slate-50 dark:focus-visible:bg-slate-800 ${activeTab === 'details' ? 'border-blue-500 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'}`}
                            >
                                <FileText size={16} className="sm:mr-2" />
                                <span className="hidden sm:inline">Detalhes</span>
                            </button>
                            <button
                                role="tab"
                                aria-selected={activeTab === 'history'}
                                aria-controls="tabpanel-history"
                                onClick={() => setActiveTab('history')}
                                className={`flex-1 py-3 px-1 text-sm font-medium flex items-center justify-center border-b-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:bg-slate-50 dark:focus-visible:bg-slate-800 ${activeTab === 'history' ? 'border-blue-500 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'}`}
                            >
                                <History size={16} className="sm:mr-2" />
                                <span className="hidden sm:inline">Histórico</span>
                            </button>
                        </nav>
                    </div>
                )}

                <div className="flex-grow overflow-y-auto p-6">
                    {activeTab === 'details' || !isViewMode ? (
                        <form id="pendency-form" onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="md:col-span-1">
                                    <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Descrição da Pendência</label>
                                    <input type="text" name="descricao" value={formData.descricao || ''} onChange={handleInputChange} className="input-style px-4 w-full" maxLength="300" required disabled={!canEdit && isViewMode} />
                                </div>
                                <div className="md:col-span-1">
                                   <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Coordenadas Geográficas (Opcional)</label>
                                   <input type="text" name="coordenadas" value={formData.coordenadas || ''} onChange={handleInputChange} className="input-style px-4 w-full" placeholder="Ex: -22.8845, -48.4439" disabled={!canEdit && isViewMode} />
                                </div>
                                {canSetPendencyType && (
                                    <div className="flex flex-col justify-end md:col-span-1">
                                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Tipo</label>
                                        <div className="flex items-center gap-2 p-2 rounded-md bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600">
                                            <span className={`text-sm ${formData.tipo === 'Externa' ? 'font-semibold text-slate-700 dark:text-slate-200' : 'text-slate-400 dark:text-slate-500'}`}>
                                                Externa
                                            </span>
                                            <label htmlFor="pendency-type-toggle" className="relative inline-flex items-center cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    id="pendency-type-toggle"
                                                    className="sr-only peer"
                                                    checked={formData.tipo === 'Administrativa'}
                                                    onChange={(e) => handleTypeChange(e.target.checked)}
                                                    disabled={!canEdit && isViewMode}
                                                />
                                                <div className="w-11 h-6 bg-slate-200 dark:bg-slate-700 rounded-full peer peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-blue-600"></div>
                                            </label>
                                            <span className={`text-sm ${formData.tipo === 'Administrativa' ? 'font-semibold text-slate-700 dark:text-slate-200' : 'text-slate-400 dark:text-slate-500'}`}>
                                                Administrativa
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Cidade</label>
                                    <select name="cidade" value={formData.cidade || ''} onChange={handleInputChange} className="input-style px-4 w-full" required disabled={!canEdit && isViewMode}>
                                        {cities.map(c => <option key={c.id} value={c.nome}>{c.nome}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Prioridade</label>
                                    <select name="prioridade" value={formData.prioridade || 'Média'} onChange={handleInputChange} className="input-style px-4 w-full" required disabled={!canEdit && isViewMode}>
                                        <option value="Baixa">Baixa</option>
                                        <option value="Média">Média</option>
                                        <option value="Alta">Alta</option>
                                    </select>
                                </div>
                                <div>
                                   <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Status</label>
                                   <select name="status" value={formData.status || 'Pendente'} onChange={handleInputChange} className="input-style px-4 w-full" required>
                                       <option value="Pendente">Pendente</option>
                                       <option value="Executado">Executado</option>
                                   </select>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Atribuído para (Executado por)</label>
                                    <select name="executadoPor" value={formData.executadoPor || ''} onChange={handleInputChange} className="input-style px-4 w-full" disabled={!canEdit && isViewMode}>
                                        <option value="">Ninguém</option>
                                        {users.filter(u => u.role !== 'pending' && u.email).map(u => <option key={u.id} value={JSON.stringify({uid: u.id, displayName: u.displayName})}>{u.displayName || u.email}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Data de Execução</label>
                                    <input type="date" name="dataExecucao" value={formData.dataExecucao || ''} onChange={handleInputChange} className="input-style px-4 w-full" disabled={formData.status !== 'Executado' || isProcessing} />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Observações / Comentário Inicial</label>
                                {(!canEdit && isViewMode) ? (
                                    <div className="prose dark:prose-invert max-w-none p-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md min-h-[84px]">
                                        <ReactMarkdown>{formData.observacoes || '*Nenhuma observação fornecida.*'}</ReactMarkdown>
                                    </div>
                                ) : (
                                    <div>
                                        <ObservationToolbar
                                            textareaRef={observacoesTextareaRef}
                                            textSetter={(value) => setFormData(prev => ({ ...prev, observacoes: value }))}
                                        />
                                        <textarea
                                            ref={observacoesTextareaRef}
                                            name="observacoes"
                                            value={formData.observacoes || ''}
                                            onChange={handleInputChange}
                                            className="input-style px-4 w-full rounded-t-none border-t-0 focus:ring-0"
                                            rows="3"
                                        />
                                    </div>
                                )}
                            </div>
                            <div>
                                <StyledFileInput
                                    label="Anexar novos arquivos"
                                    onChange={handleFileChange}
                                    accept="image/*,.pdf,.dwg,.kmz,.kml"
                                    isMultiple={true}
                                    files={files}
                                    disabled={!canEdit && isViewMode}
                                />
                                {isViewMode && pendency.files && pendency.files.length > 0 && (
                                    <div className="mt-4">
                                        <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Arquivos existentes:</p>
                                        <ul className="list-disc list-inside space-y-1 mt-2">
                                            {pendency.files.map((file, idx) => (
                                                <li key={idx} className="text-sm flex items-center justify-between p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800">
                                                    <button type="button" onClick={() => handleDownloadClick(file, `file-${idx}`)} className="text-blue-500 hover:underline disabled:text-slate-400 flex-grow text-left flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500 rounded p-1" aria-label={`Baixar arquivo: ${file.name}`}>
                                                        {downloading === `file-${idx}` ? <Loader className="animate-spin h-4 w-4 inline mr-2"/> : <Download size={14} className="inline mr-2"/>}
                                                        {file.name}
                                                    </button>
                                                    {canEdit && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleDeleteFile(file)}
                                                            className="p-1 text-red-500 hover:text-red-700 hover:bg-red-100 dark:hover:bg-red-900/50 rounded-full transition-colors ml-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                                                            title="Excluir arquivo"
                                                            aria-label="Excluir arquivo"
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </button>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </form>
                    ) : (
                        isHistoryLoading ? (
                            <div className="flex justify-center items-center h-48">
                                <Loader className="animate-spin h-8 w-8 text-slate-500" />
                            </div>
                        ) : (
                            <Timeline activities={activities} users={users} onUserAvatarClick={handleUserAvatarClick} />
                        )
                    )}
                </div>
                <footer className="p-4 border-t border-slate-200 dark:border-slate-700 flex justify-between items-center gap-2 sm:gap-4">
                    <div className="flex items-center gap-2">
                        {isViewMode && formData.coordenadas && (
                             <div className="flex items-center gap-2">
                                <button type="button" onClick={handleShare} className="btn-secondary flex items-center focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-slate-400" disabled={copySuccess} title={copySuccess ? 'Copiado!' : 'Compartilhar'}>
                                    <Copy className="h-5 w-5 sm:mr-2" />
                                    <span className="hidden sm:inline">{copySuccess ? 'Copiado!' : 'Compartilhar'}</span>
                                </button>
                                <button type="button" onClick={handleOpenMap} className="btn-secondary flex items-center focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-slate-400" title="Abrir no Mapa">
                                    <IconMap className="h-5 w-5 sm:mr-2" />
                                    <span className="hidden sm:inline">Abrir no Mapa</span>
                                </button>
                             </div>
                        )}
                        {isViewMode && canEdit && (
                            <button
                                type="button"
                                onClick={() => setShowDeleteConfirm(true)}
                                disabled={isProcessing}
                                className="p-2 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/50 rounded-full transition-colors disabled:opacity-50 sm:ml-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                                title="Excluir Pendência"
                                aria-label="Excluir Pendência"
                            >
                                <Trash2 className="h-5 w-5" />
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-x-2 sm:gap-x-4">
                        <button type="button" onClick={onClose} className="btn-secondary focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-slate-400" title="Cancelar" aria-label="Cancelar">
                            <span className="hidden sm:inline">Cancelar</span>
                            <IconX className="sm:hidden h-5 w-5" />
                        </button>
                        {activeTab === 'details' && (
                            <button type="submit" form="pendency-form" disabled={isProcessing} className="btn-primary flex items-center" title="Salvar">
                                <Save className="h-5 w-5 sm:mr-2" />
                                <span className="hidden sm:inline">{isProcessing ? 'Salvando...' : 'Salvar'}</span>
                            </button>
                        )}
                    </div>
                </footer>
            </div>
            {showAttachmentPopover && (
                <div className="fixed inset-0 bg-black/40 z-[1100] flex items-center justify-center p-4" onClick={() => setShowAttachmentPopover(false)}>
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
                        <header className="p-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
                            <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                                <Paperclip className="w-4 h-4 text-blue-500" />
                                Anexar Item do Sistema
                            </h3>
                            <button type="button" onClick={() => setShowAttachmentPopover(false)} className="p-1.5 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800"><IconX className="w-4 h-4" /></button>
                        </header>
                        <div className="p-4 flex flex-col gap-3 overflow-hidden flex-1">
                            <div className="flex gap-2 p-0.5 bg-slate-100 dark:bg-slate-800 rounded-lg">
                                <button type="button" onClick={() => { setAttachmentType('project'); setAttachmentSearch(''); }} className={`flex-1 py-1.5 text-xs font-semibold rounded-md transition-all ${attachmentType === 'project' ? 'bg-white dark:bg-slate-700 shadow text-blue-600 dark:text-blue-400' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700'}`}>Projetos</button>
                                <button type="button" onClick={() => { setAttachmentType('pendency'); setAttachmentSearch(''); }} className={`flex-1 py-1.5 text-xs font-semibold rounded-md transition-all ${attachmentType === 'pendency' ? 'bg-white dark:bg-slate-700 shadow text-blue-600 dark:text-blue-400' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700'}`}>Pendências</button>
                            </div>
                            <input
                                type="text"
                                value={attachmentSearch}
                                onChange={(e) => setAttachmentSearch(e.target.value)}
                                placeholder={attachmentType === 'project' ? "Pesquisar por protocolo, cidade..." : "Pesquisar por descrição, cidade..."}
                                className="input-style px-3 py-1.5 text-xs w-full"
                            />
                            <div className="flex-1 overflow-y-auto pr-1 flex flex-col gap-1.5 min-h-0">
                                {filteredAttachments.length > 0 ? (
                                    filteredAttachments.map(item => (
                                        <button
                                            key={item.id}
                                            type="button"
                                            onClick={() => handleInsertAttachment(item)}
                                            className="w-full text-left p-2.5 rounded-lg border border-slate-200 dark:border-slate-800 hover:border-blue-500 dark:hover:border-blue-400 hover:bg-blue-50/20 dark:hover:bg-blue-900/10 flex items-center justify-between group transition-all"
                                        >
                                            <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                                                <span className="text-xs font-bold text-slate-800 dark:text-slate-100 truncate">
                                                    {attachmentType === 'project' ? item.protocolo : item.descricao}
                                                </span>
                                                <span className="text-[10px] text-slate-400 truncate">
                                                    {item.cidade} {item.local ? `- ${item.local}` : ''}
                                                </span>
                                            </div>
                                            <Send className="w-3.5 h-3.5 text-slate-300 group-hover:text-blue-500 transition-colors flex-shrink-0" />
                                        </button>
                                    ))
                                ) : (
                                    <p className="text-xs text-slate-400 text-center py-8">Nenhum item encontrado.</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PendencyModal;
